import { cpfValidator } from 'helpers/cpfValidator';
import { Dispatch, SetStateAction, useState } from 'react';
import * as yup from 'yup';
import { UserForm } from '../form/UserForm';

export type UserValidation = {
  document_number?: string;
  rg?: string;
  name?: string;
  phone?: string;
  address?: string;
  username?: string;
  email?: string;
  password?: string;
  password_confirmation?: string;
};

type UseUserValidation = [
  UserValidation,
  Dispatch<SetStateAction<UserValidation>>,
  (user: UserForm, type: 'edit' | 'new') => Promise<void>,
];

export function useUserValidation(): UseUserValidation {
  async function handleValidation(user: UserForm, type: 'edit' | 'new') {
    const schema = yup.object().shape({
      password: yup.string().when([], {
        is: () => type === 'new',
        then: schema => schema.required('Informe a nova senha').min(8, 'A senha deve ter no mínimo 8 caracteres'),
        otherwise: schema => schema.notRequired(),
      }),
      password_confirmation: yup.string().when('password', {
        is: (val: string | undefined) => !!val,
        then: schema => schema.oneOf([yup.ref('password')], 'Nova senha não confere'),
        otherwise: schema => schema.notRequired(),
      }),
      document_number: yup
        .string()
        .required('CPF é obrigatório')
        .transform((value, originalValue) => {
          return originalValue ? originalValue.replace(/\D/g, '') : '';
        })
        .test('docValidation', `CPF inválido`, value => {
          if (!value) return false;

          const cpf = cpfValidator(value);

          return cpf;
        }),
      rg: yup.string().required('RG é obrigatório'),
      name: yup.string().required('O nome é obrigatório').min(10, 'O nome deve ter no mínimo 10 caracteres'),
      email: yup.string().required('O e-mail é obrigatório').email('O e-mail deve ser válido'),
      username: yup
        .string()
        .required('O nome de usuário é obrigatório')
        .min(5, 'O nome de usuário deve ter no mínimo 5 caracteres'),
    });

    try {
      await schema.validate(user);
    } catch (err) {
      const error = err as yup.ValidationError;
      if (error.path && error.message) {
        setValidation({
          [error.path]: error.message,
        });
      }
      throw new Error(err as string);
    }
  }

  const [validation, setValidation] = useState<UserValidation>({});
  return [validation, setValidation, handleValidation];
}
